import {
  alternateImageRepoPaths,
  imageRepoPath,
  legacySiblingImagePaths,
} from './itemImages';
import type { GitHubService } from '../services/GitHubService';

/** `photo.jpg` → `photo.thumb.jpg` (thumbnails are always JPEG). */
export function thumbnailFilename(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  return `${base}.thumb.jpg`;
}

export function thumbnailRepoPath(mdPath: string, imageFilename: string): string {
  return imageRepoPath(mdPath, thumbnailFilename(imageFilename));
}

export function thumbnailRepoPathFromFull(fullRepoPath: string): string {
  const slash = fullRepoPath.lastIndexOf('/');
  if (slash < 0) return thumbnailFilename(fullRepoPath);
  return `${fullRepoPath.slice(0, slash + 1)}${thumbnailFilename(fullRepoPath.slice(slash + 1))}`;
}

/** Gallery/lightbox: try thumbnails then full, including legacy folder spellings. */
export function imageLoadRepoPaths(
  repoPath: string,
  includeThumbnails: boolean,
  mdPath?: string
): string[] {
  const bases = alternateImageRepoPaths(repoPath, mdPath);
  if (!includeThumbnails) return bases;
  const out: string[] = [];
  for (const base of bases) {
    const leaf = base.split('/').pop() ?? '';
    if (/\.thumb\.jpe?g$/i.test(leaf)) {
      out.push(base);
      continue;
    }
    out.push(thumbnailRepoPathFromFull(base), base);
  }
  return [...new Set(out)];
}

export async function imageLoadRepoPathsAsync(
  github: GitHubService,
  repoPath: string,
  includeThumbnails: boolean,
  mdPath?: string
): Promise<string[]> {
  const paths = imageLoadRepoPaths(repoPath, includeThumbnails, mdPath);
  const filename = repoPath.split('/').pop();
  if (!mdPath || !filename) return paths;

  const siblings = await legacySiblingImagePaths(github, mdPath, filename);
  const out = [...paths];
  for (const base of siblings) {
    if (includeThumbnails) {
      out.push(thumbnailRepoPathFromFull(base), base);
    } else {
      out.push(base);
    }
  }
  return [...new Set(out)];
}

/** Resize for gallery tiles (~5.5rem); full image loads only in the lightbox. */
export async function createThumbnailFromFile(file: File, maxEdge = 400): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', 0.82)
    );
    if (!blob) throw new Error('Could not encode thumbnail');
    return blob;
  } finally {
    bitmap.close();
  }
}
